import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { categoryApi } from '../../utils/api';

function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '', color: '#2457A8' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await categoryApi.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.slug) {
      toast.error('श्रेणीको नाम र स्लग आवश्यक छ।');
      return;
    }
    try {
      if (editing) {
        await categoryApi.update(editing, form);
        toast.success('श्रेणी अद्यावधिक भयो।');
      } else {
        await categoryApi.create(form);
        toast.success('श्रेणी थपियो।');
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', slug: '', color: '#2457A8' });
      await fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('श्रेणी सुरक्षित गर्न असफल भयो।');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('के तपाईं यो श्रेणी मेटाउन निश्चित हुनुहुन्छ?')) return;
    try {
      await categoryApi.delete(id);
      toast.success('श्रेणी मेटियो।');
      await fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('श्रेणी मेटाउन असफल भयो।');
    }
  };

  const handleEdit = (category) => {
    setEditing(category.id);
    setForm({ name: category.name, slug: category.slug, color: category.color || '#2457A8' });
    setShowForm(true);
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">श्रेणीहरू</h2>
        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-4 py-2.5 rounded-full hover:bg-crimson-dark"
          onClick={() => { setShowForm(true); setEditing(null); setForm({ name: '', slug: '', color: '#2457A8' }); }}
        >
          <Plus size={15} /> नयाँ थप्नुहोस्
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-line rounded-[14px] p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              श्रेणीको नाम
              <input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="श्रेणीको नाम" />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              स्लग
              <input className={inputCls} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })} placeholder="slug" />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              रङ
              <input type="color" className="h-[42px] w-full rounded-lg border border-line bg-white cursor-pointer" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
            </label>
          </div>
          <div className="flex justify-end gap-2.5 mt-4">
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft" onClick={() => { setShowForm(false); setEditing(null); }}>
              रद्द गर्नुहोस्
            </button>
            <button className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark" onClick={handleSubmit}>
              {editing ? 'अद्यावधिक गर्नुहोस्' : 'थप्नुहोस्'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-3 bg-white border border-line rounded-xl p-3">
            <span className="w-5 h-5 rounded flex-shrink-0" style={{ background: cat.color || '#2457A8' }} />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold">{cat.name}</p>
              <span className="text-xs text-muted">{cat.slug} • {cat.news_count || 0} समाचार</span>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-paper-soft" onClick={() => handleEdit(cat)} aria-label="Edit">
                <Pencil size={16} />
              </button>
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-red-50 hover:text-crimson" onClick={() => handleDelete(cat.id)} aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {categories.length === 0 && <p className="text-muted py-4">कुनै श्रेणी छैन।</p>}
      </div>
    </div>
  );
}

export default AdminCategories;